import React, { JSX, useState, useRef } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { processCoverImage } from "../../lib/imageUtils";
import "./CreateBoardModal.css";

type CreateBoardModalProps = {
  open: boolean;
  onClose: (created?: boolean) => void;
};

export default function CreateBoardModal({ open, onClose }: CreateBoardModalProps): JSX.Element | null {
  const { user } = useAuth();
  const [boardName, setBoardName] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 10; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  React.useEffect(() => {
    if (open) {
      setInviteCode(generateCode());
      setBoardName("");
      setCoverImage(null);
      setCoverPreview(null);
    }
  }, [open]);

  // Clean up object URL to prevent memory leaks
  React.useEffect(() => {
    return () => {
      if (coverPreview) {
        URL.revokeObjectURL(coverPreview);
      }
    };
  }, [coverPreview]);

  if (!open) return null;

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const originalFile = e.target.files[0];
      setCoverPreview(URL.createObjectURL(originalFile));
      const processedFile = await processCoverImage(originalFile);
      setCoverImage(processedFile);
    }
  };

  const handleRemoveImage = () => {
    setCoverImage(null);
    setCoverPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      setLoading(true);
      let coverImageUrl = null;

      // 1. Upload Cover Image if selected
      if (coverImage) {
        // Always use .jpg since processCoverImage converts to JPEG
        const filePath = `${Math.random()}.jpg`;

        const { error: uploadError } = await supabase.storage
          .from('board-covers')
          .upload(filePath, coverImage);

        if (uploadError) throw uploadError;

        const { data } = supabase.storage
          .from('board-covers')
          .getPublicUrl(filePath);

        coverImageUrl = data.publicUrl;
      }

      // 2. Create Board
      const { data: boardData, error: boardError } = await supabase
        .from('boards')
        .insert({
          name: boardName,
          invite_code: inviteCode,
          created_by: user.id,
          cover_image_url: coverImageUrl
        })
        .select()
        .single();

      if (boardError) throw boardError;

      // 3. Add Creator as Member (Admin)
      const { error: memberError } = await supabase
        .from('board_members')
        .insert({
          board_id: boardData.id,
          user_id: user.id,
          role: 'admin'
        });

      if (memberError) throw memberError;

      onClose(true);
    } catch (error: any) {
      console.error("Error creating board:", error);
      alert(`Failed to create board: ${error.message || JSON.stringify(error)}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-board-modal">
      <div className="overlay" onClick={() => onClose(false)}>
        <div className="modal-container" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2 className="modal-title">Create New Board</h2>
            <button className="modal-close-button" onClick={() => onClose(false)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="label">Board Name</label>
              <input
                className="input"
                value={boardName}
                onChange={(e) => setBoardName(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label className="label">Invite Code</label>
              <div style={{ position: 'relative' }}>
                <input
                  className="input"
                  value={inviteCode}
                  readOnly
                  style={{ backgroundColor: '#f3f4f6', color: '#6b7280', cursor: 'default' }}
                />
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(inviteCode);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: copied ? '#10b981' : '#137fec',
                    fontWeight: 600,
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'color 0.2s'
                  }}
                >
                  {copied ? 'COPIED!' : 'COPY'}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="label">Cover Image</label>
              <div
                className="upload-area"
                onClick={() => fileInputRef.current?.click()}
                style={{
                  backgroundImage: coverPreview ? `url(${coverPreview})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  border: coverPreview ? '2px solid transparent' : '2px dashed #e2e8f0'
                }}
              >
                {!coverPreview && (
                  <>
                    <svg className="upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="upload-text">Upload an image</span>
                  </>
                )}
                {coverPreview && (
                  <button
                    type="button"
                    className="remove-image-button"
                    onClick={(e) => { e.stopPropagation(); handleRemoveImage(); }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageSelect}
                  accept="image/*"
                  style={{ display: 'none' }}
                />
              </div>
            </div>

            <button type="submit" className="create-button" disabled={loading || boardName.trim() === ''}>
              {loading ? 'Creating...' : 'Create Board'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
